/**
 * ==========================================
 * BREADCRUMB CONTEXT
 * ==========================================
 * File: frontend/src/contexts/BreadcrumbContext.tsx
 *
 * Description: Provides breadcrumb metadata across the app
 * Features: Allows pages to set their title/metadata for breadcrumb generation
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// ==========================================
// TYPES
// ==========================================

export interface BreadcrumbMetadata {
  lessonTitle?: string;
  lessonId?: string;
  className?: string;
  classId?: string;
  learnerName?: string;
  customTitle?: string;
  fromLibrary?: boolean;
  libraryPath?: string;

  // Tab support
  activeTab?: string;
  activeTabLabel?: string;

  // Assignment support
  assignmentTitle?: string;
  assignmentId?: string;
}

interface BreadcrumbContextType {
  metadata: BreadcrumbMetadata;
  setMetadata: (metadata: Partial<BreadcrumbMetadata>) => void;
  clearMetadata: () => void;
}

// ==========================================
// CONTEXT CREATION
// ==========================================

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

// ==========================================
// PROVIDER COMPONENT
// ==========================================

interface BreadcrumbProviderProps {
  children: ReactNode;
}

export const BreadcrumbProvider: React.FC<BreadcrumbProviderProps> = ({ children }) => {
  const [metadata, setMetadataState] = useState<BreadcrumbMetadata>({});

  const setMetadata = useCallback((newMetadata: Partial<BreadcrumbMetadata>) => {
    setMetadataState(prev => ({
      ...prev,
      ...newMetadata
    }));
  }, []);

  const clearMetadata = useCallback(() => {
    setMetadataState({});
  }, []);

  return (
    <BreadcrumbContext.Provider value={{ metadata, setMetadata, clearMetadata }}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

// ==========================================
// HOOK
// ==========================================

export const useBreadcrumbContext = (): BreadcrumbContextType => {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumbContext must be used within BreadcrumbProvider');
  }
  return context;
};
